import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import { executionDependencyNames } from '@de/utils/data-sources/readyWhen';

/**
 * Group `names` into dependency levels so that every source within a level can
 * run concurrently.
 *
 * A source lands in level 0 when it declares no `dependsOn` entry that is also
 * in `names`; otherwise it lands one level below its deepest in-subset
 * dependency. Running the levels in order therefore preserves the guarantee
 * every fetch path relies on: a source never starts before the sources whose
 * resolved data it interpolates.
 *
 * `ordered` must be a topological order of `dataSources` (dependencies first,
 * as produced by `topologicalSort`), which lets a single pass resolve every
 * level. Output order within a level follows `ordered`, so the result is
 * deterministic for a given graph.
 *
 * Dependencies *outside* `names` are ignored rather than pulled in: the caller
 * has already chosen the subset to re-run, and an out-of-subset dependency
 * keeps whatever it last resolved to. Names absent from `dataSources` are
 * dropped, matching `refetchSource`'s no-op-on-unknown-name contract.
 */
export function dependencyLevels(
  dataSources: Readonly<Record<string, DataSourceDeclaration>>,
  ordered: readonly string[],
  names: readonly string[],
): readonly (readonly string[])[] {
  const subset = new Set(
    names.filter((name) =>
      Object.prototype.hasOwnProperty.call(dataSources, name),
    ),
  );
  if (subset.size === 0) {
    return [];
  }

  const levelByName = new Map<string, number>();
  for (const name of ordered) {
    if (!subset.has(name)) {
      continue;
    }
    let level = 0;
    for (const dep of executionDependencyNames(dataSources[name])) {
      const depLevel = levelByName.get(dep);
      if (depLevel !== undefined) {
        level = Math.max(level, depLevel + 1);
      }
    }
    levelByName.set(name, level);
  }

  const levels: string[][] = [];
  for (const name of ordered) {
    const level = levelByName.get(name);
    if (level === undefined) {
      continue;
    }
    const bucket = levels[level];
    if (bucket === undefined) {
      levels[level] = [name];
    } else {
      bucket.push(name);
    }
  }

  return levels;
}
